import { DEFERRED_AI_ACTIONS_V1 } from "@adpilot/shared";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import type { ActionOperationDetail } from "@/lib/ai-action-operation-api";
import { useFormatters } from "@/lib/format";
import { formatBeijingDateTime } from "@/lib/time";

type OperationStatus = ActionOperationDetail["status"];
type StepStatus = ActionOperationDetail["steps"][number]["status"];

const operationStatusVariant: Record<
  OperationStatus,
  "neutral" | "success" | "warning" | "info" | "destructive"
> = {
  pending_approval: "warning",
  approved: "info",
  rejected: "destructive",
  cancelled: "neutral",
  queued: "info",
  executing: "info",
  succeeded: "success",
  failed: "destructive",
  partial_success: "warning",
  reconciliation_required: "warning",
};

const stepStatusVariant: Record<
  StepStatus,
  "neutral" | "success" | "warning" | "info" | "destructive"
> = {
  pending: "neutral",
  executing: "info",
  succeeded: "success",
  failed: "destructive",
  result_unknown: "warning",
};

interface ActionOperationRecordProps {
  detail: ActionOperationDetail;
  canApprove?: boolean;
  canExecute?: boolean;
  canCancel?: boolean;
  busy?: boolean;
  onDecision?: (decision: "approve" | "reject") => void;
  onExecute?: () => void;
  onCancel?: () => void;
  onReconcile?: () => void;
  onRefresh?: () => void;
}

/** Unreviewed payloads stay private; commands expose consent separately from execution. */
export function ActionOperationRecord({
  detail,
  canApprove = false,
  canExecute = false,
  canCancel = canExecute,
  busy = false,
  onDecision,
  onExecute,
  onCancel,
  onReconcile,
  onRefresh,
}: ActionOperationRecordProps): React.ReactElement {
  const { t } = useTranslation(["aiOperations", "smartCreation"]);
  const { locale } = useFormatters();
  const events = [...detail.events].sort(
    (left, right) => left.operationVersion - right.operationVersion,
  );
  const steps = [...detail.steps].sort((left, right) => left.ordinal - right.ordinal);
  const deferred = DEFERRED_AI_ACTIONS_V1.some((action) => action === detail.proposal.action);

  return (
    <div className="flex flex-col gap-4" data-action-operation-record>
      <Card className="gap-0 py-0">
        <CardHeader className="flex flex-wrap items-start justify-between gap-3 border-b border-line px-5 py-4">
          <div className="flex flex-col gap-1">
            <CardTitle className="text-base">
              {t(`smartCreation:review.aiActions.${detail.proposal.action}`)}
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              {t("aiOperations:actionOperation.scope", {
                count: detail.proposal.campaignIds.length,
              })}
            </p>
          </div>
          <Badge variant={operationStatusVariant[detail.status]}>
            {t(`aiOperations:actionOperation.status.${detail.status}`)}
          </Badge>
        </CardHeader>
        <CardContent className="grid gap-4 px-5 py-4 sm:grid-cols-3">
          <Fact
            label={t("aiOperations:actionOperation.acceptedAt")}
            value={formatBeijingDateTime(detail.createdAt, locale)}
          />
          <Fact
            label={t("aiOperations:actionOperation.decisionAt")}
            value={detail.decidedAt ? formatBeijingDateTime(detail.decidedAt, locale) : "—"}
          />
          <Fact
            label={t("aiOperations:actionOperation.updatedAt")}
            value={formatBeijingDateTime(detail.updatedAt, locale)}
          />
        </CardContent>
      </Card>

      <p className="px-1 text-xs leading-5 text-muted-foreground">
        {t("aiOperations:actionOperation.localOnly")}
      </p>

      <div className="flex flex-wrap items-center gap-2" aria-busy={busy}>
        {detail.status === "pending_approval" && canApprove && onDecision ? (
          <>
            <Button
              size="sm"
              disabled={busy || deferred}
              data-operation-command="approve"
              onClick={() => onDecision("approve")}
            >
              {t("aiOperations:actionOperation.commands.approve")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              data-operation-command="reject"
              onClick={() => onDecision("reject")}
            >
              {t("aiOperations:actionOperation.commands.reject")}
            </Button>
          </>
        ) : null}
        {detail.status === "approved" && canExecute && onExecute ? (
          <>
            <Button
              size="sm"
              disabled={busy || !detail.executionEnabled || deferred}
              data-operation-command="execute"
              onClick={onExecute}
            >
              {t("aiOperations:actionOperation.commands.execute")}
            </Button>
            {!detail.executionEnabled ? (
              <span className="text-xs text-muted-foreground">
                {t("aiOperations:actionOperation.commands.disabled")}
              </span>
            ) : null}
          </>
        ) : null}
        {canCancel &&
        onCancel &&
        ["pending_approval", "approved", "queued"].includes(detail.status) ? (
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            data-operation-command="cancel"
            onClick={onCancel}
          >
            {t("aiOperations:actionOperation.commands.cancel")}
          </Button>
        ) : null}
        {detail.status === "reconciliation_required" && canExecute && onReconcile ? (
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            data-operation-command="reconcile"
            onClick={onReconcile}
          >
            {t("aiOperations:actionOperation.commands.reconcile")}
          </Button>
        ) : null}
        {onRefresh ? (
          <Button size="sm" variant="ghost" disabled={busy} onClick={onRefresh}>
            {t("aiOperations:actionOperation.commands.refresh")}
          </Button>
        ) : null}
        {busy ? (
          <Spinner
            className="size-4 text-muted-foreground"
            aria-label={t("common:state.loading")}
          />
        ) : null}
      </div>

      <DecisionEvidence evidence={detail.proposal.evidence} />

      <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1.35fr)_minmax(280px,1fr)]">
        <Card className="gap-0 py-0">
          <CardHeader className="border-b border-line px-5 py-4">
            <CardTitle>{t("aiOperations:actionOperation.stepsTitle")}</CardTitle>
            <p className="text-xs text-muted-foreground">
              {t("aiOperations:actionOperation.stepsDescription")}
            </p>
          </CardHeader>
          <CardContent className="px-5 py-2">
            <ol className="divide-y divide-line">
              {steps.map((step) => (
                <li key={step.id} className="flex gap-3 py-4" data-action-operation-step>
                  <span
                    className="grid size-7 shrink-0 place-items-center rounded-full border border-line bg-subtle text-xs font-semibold tabular-nums text-ink"
                    aria-hidden
                  >
                    {step.ordinal}
                  </span>
                  <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <h3 className="text-sm font-medium text-ink">
                        {t("aiOperations:actionOperation.stepNumber", { number: step.ordinal })}
                      </h3>
                      <Badge variant={stepStatusVariant[step.status]}>
                        {t(`aiOperations:actionOperation.stepStatus.${step.status}`)}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {t("aiOperations:actionOperation.campaignRef", {
                        id: step.campaignId.slice(0, 8).toUpperCase(),
                      })}
                    </p>
                    <ChangeSummary change={step.plannedChange} />
                    <p className="text-xs text-muted-foreground">
                      {step.finishedAt
                        ? t("aiOperations:actionOperation.finishedAt", {
                            time: formatBeijingDateTime(step.finishedAt, locale),
                          })
                        : step.startedAt
                          ? t("aiOperations:actionOperation.startedAt", {
                              time: formatBeijingDateTime(step.startedAt, locale),
                            })
                          : t("aiOperations:actionOperation.notStarted")}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>

        <Card className="gap-0 py-0">
          <CardHeader className="border-b border-line px-5 py-4">
            <CardTitle>{t("aiOperations:actionOperation.eventsTitle")}</CardTitle>
            <p className="text-xs text-muted-foreground">
              {t("aiOperations:actionOperation.eventsDescription")}
            </p>
          </CardHeader>
          <CardContent className="px-5 py-2">
            <ol className="divide-y divide-line">
              {events.map((event) => (
                <li key={event.id} className="flex gap-3 py-3" data-action-operation-event>
                  <span
                    className="mt-1.5 size-2 shrink-0 rounded-full bg-muted-foreground"
                    aria-hidden
                  />
                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <p className="text-sm font-medium text-ink">
                      {t(`aiOperations:actionOperation.event.${event.eventType}`)}
                      {event.stepOrdinal !== null
                        ? ` · ${t("aiOperations:actionOperation.stepNumber", {
                            number: event.stepOrdinal,
                          })}`
                        : null}
                    </p>
                    <time
                      className="text-xs tabular-nums text-muted-foreground"
                      dateTime={event.createdAt}
                    >
                      {formatBeijingDateTime(event.createdAt, locale)}
                    </time>
                    {event.eventType === "step_failed" ||
                    event.eventType === "step_result_unknown" ? (
                      <OperationFailure detail={event.detail} />
                    ) : null}
                  </div>
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

const failureReasons: Record<string, string> = {
  "ai.action.policy_changed": "configurationChanged",
  "ai.action.objective_changed": "configurationChanged",
  "ai.action.scope_changed": "configurationChanged",
  "ai.action.writes_disabled": "configurationChanged",
  execution_disabled: "configurationChanged",
  "ai.action.old_value_changed": "valuesChanged",
  "ai.action.budget_rule_or_value_changed": "valuesChanged",
  "ai.action.bid_out_of_bounds": "bounds",
  "ai.action.budget_out_of_bounds": "bounds",
  "ai.action.placement_out_of_bounds": "bounds",
  "ai.action.excluded": "protectedProduct",
  "ai.action.own_product_excluded": "protectedProduct",
  "ai.action.mutation_busy": "busy",
  "campaign.mutation.busy": "busy",
  "ai.action.dependency_unconfirmed": "dependency",
  "ai.action.destination_products_changed": "dependency",
  "ai.action.destination_relationship_changed": "dependency",
  "ai.action.duplicate_target": "duplicate",
  evidence_stale: "dataStale",
  preflight_error: "readback",
  "ai.action.preflight_unavailable": "readback",
  "ai.action.readback_unavailable": "readback",
  "ai.action.dispatch_deadline_exceeded": "readback",
  readback_unavailable: "uncertain",
  readback_unconfirmed: "uncertain",
  write_result_unknown: "uncertain",
  "ai.action.write_result_unknown": "uncertain",
};
function OperationFailure({ detail }: { detail: Record<string, unknown> }): React.ReactElement {
  const { t } = useTranslation("aiOperations");
  const code = detail.reasonCode ?? detail.errorCode;
  const reason = typeof code === "string" ? failureReasons[code] : undefined;
  return (
    <p className="text-xs leading-5 text-muted-foreground">
      {t(`actionOperation.failure.${reason ?? "generic"}`)}
    </p>
  );
}

const MetricsSchema = z.object({
  clicks: z.number().int().nonnegative(),
  orders: z.number().int().nonnegative(),
  spend: z.number().finite().nonnegative(),
  sales: z.number().finite().nonnegative(),
});
const DisplayEvidenceSchema = z.object({
  startDate: z.string().date().optional(),
  endDate: z.string().date().optional(),
  targetAcos: z.number().finite().positive().max(2).optional(),
  metrics: MetricsSchema.optional(),
  recent14Days: MetricsSchema.optional(),
  clicks: z.number().int().nonnegative().optional(),
  orders: z.number().int().nonnegative().optional(),
  spend: z.number().finite().nonnegative().optional(),
  sales: z.number().finite().nonnegative().optional(),
  budgetBoost: z.object({ durationDays: z.number().int().min(1).max(7) }).optional(),
});

/** Only reviewed fields enter the approval UI; raw rationale and technical payloads stay private. */
function DecisionEvidence({ evidence }: { evidence: unknown }): React.ReactElement | null {
  const { t } = useTranslation("aiOperations");
  const { formatNumber, formatPercent } = useFormatters();
  const parsed = DisplayEvidenceSchema.safeParse(evidence);
  if (!parsed.success) return null;
  const value = parsed.data;
  const topLevel = MetricsSchema.safeParse(value);
  const metrics = value.metrics ?? value.recent14Days ?? (topLevel.success ? topLevel.data : null);
  if (!metrics && !value.targetAcos && !value.startDate && !value.budgetBoost) return null;
  return (
    <section
      className="flex flex-col gap-3 rounded-lg border border-line bg-surface px-5 py-4"
      data-operation-evidence
    >
      <h2 className="text-sm font-semibold text-ink">{t("actionOperation.evidence.title")}</h2>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {value.startDate && value.endDate ? (
          <Fact
            label={t("actionOperation.evidence.window")}
            value={`${value.startDate} – ${value.endDate}`}
          />
        ) : value.recent14Days ? (
          <Fact
            label={t("actionOperation.evidence.window")}
            value={t("actionOperation.evidence.recentWindow")}
          />
        ) : null}
        {value.targetAcos ? (
          <Fact
            label={t("actionOperation.evidence.targetAcos")}
            value={formatPercent(value.targetAcos)}
          />
        ) : null}
        {metrics ? (
          <>
            <Fact
              label={t("actionOperation.evidence.acos")}
              value={metrics.sales > 0 ? formatPercent(metrics.spend / metrics.sales) : "—"}
            />
            <Fact
              label={t("actionOperation.evidence.orders")}
              value={formatNumber(metrics.orders)}
            />
            <Fact
              label={t("actionOperation.evidence.clicks")}
              value={formatNumber(metrics.clicks)}
            />
            <Fact
              label={t("actionOperation.evidence.spend")}
              value={formatNumber(metrics.spend, { maximumFractionDigits: 2 })}
            />
            <Fact
              label={t("actionOperation.evidence.sales")}
              value={formatNumber(metrics.sales, { maximumFractionDigits: 2 })}
            />
          </>
        ) : null}
        {value.budgetBoost ? (
          <Fact
            label={t("actionOperation.evidence.duration")}
            value={t("actionOperation.evidence.days", { count: value.budgetBoost.durationDays })}
          />
        ) : null}
      </div>
    </section>
  );
}

function ChangeSummary({ change }: { change: Record<string, unknown> }): React.ReactElement | null {
  const { t } = useTranslation("aiOperations");
  const kind = change.kind;
  if (typeof kind !== "string") return null;
  let value: string | null = null;
  if (
    (kind === "boost_budget" || kind === "restore_budget") &&
    typeof change.expectedBudget === "number" &&
    typeof change.desiredBudget === "number"
  ) {
    value = `${change.expectedBudget} → ${change.desiredBudget}`;
  } else if (kind === "pause_campaign" || kind === "pause_target") {
    value = t("actionOperation.change.pause");
  } else if (
    [
      "add_positive_keyword",
      "add_negative_keyword",
      "add_positive_product_target",
      "add_negative_product_target",
    ].includes(kind)
  ) {
    const target = kind.endsWith("keyword") ? change.keywordText : change.asin;
    if (typeof target === "string") value = target;
    if (value && typeof change.bid === "number")
      value += ` · ${t("actionOperation.change.bid")} ${change.bid}`;
  } else if (
    kind === "set_placement_bid" &&
    Array.isArray(change.desiredPlacements) &&
    Array.isArray(change.expectedPlacements)
  ) {
    const before = change.expectedPlacements;
    value = change.desiredPlacements
      .flatMap((item: unknown) => {
        if (
          !item ||
          typeof item !== "object" ||
          !("placement" in item) ||
          !("percentage" in item) ||
          typeof item.placement !== "string" ||
          typeof item.percentage !== "number"
        )
          return [];
        const old: unknown = before.find(
          (entry: unknown) =>
            entry !== null &&
            typeof entry === "object" &&
            "placement" in entry &&
            entry.placement === item.placement,
        );
        return old &&
          typeof old === "object" &&
          "percentage" in old &&
          typeof old.percentage === "number" &&
          old.percentage !== item.percentage
          ? [
              `${t(`actionOperation.change.placement.${item.placement}`)} ${old.percentage}% → ${item.percentage}%`,
            ]
          : [];
      })
      .join(" · ");
  }
  if (!value) return null;
  return (
    <p className="text-sm font-medium text-ink" data-operation-change>
      {value}
    </p>
  );
}

function Fact({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-ink">{value}</span>
    </div>
  );
}
